/**
 * The place a dropped pin sits on.
 *
 * A pit stop stores a name, a description and a point — and no address at all
 * (the organiser form says as much: its address box is UI-only). So "where is
 * the water station?" can only be answered by asking what is at those
 * coordinates, which is what the organiser's own location field already does
 * through Nominatim. No new service.
 *
 * Cached per coordinate for the life of the page, since a lookup that has
 * already failed must not be retried on every render, and two stops at the same
 * junction cost one request. A failure returns '' and the stop still shows its
 * name — the label is a courtesy, never the thing being read.
 */

import { useEffect, useState } from 'react';
import { reverseGeocode } from '../geocode';

const cache = new Map();

/** "Cubbon Park, Sampangirama Nagar, …, Karnataka, India" → "Cubbon Park, Sampangirama Nagar". */
export function shortPlace(label, parts = 2) {
  if (!label) return '';
  return String(label).split(',').slice(0, parts).map(s => s.trim()).filter(Boolean).join(', ');
}

export default function useReversePlace(point) {
  const key = point ? `${point.lat},${point.lng}` : '';
  const [place, setPlace] = useState(() => cache.get(key) || '');

  useEffect(() => {
    if (!key) return undefined;
    if (cache.has(key)) { setPlace(cache.get(key)); return undefined; }

    let alive = true;
    const controller = new AbortController();

    reverseGeocode(point.lat, point.lng, { signal: controller.signal }).then(found => {
      const label = found ? shortPlace(found.label) : '';
      cache.set(key, label);
      if (alive) setPlace(label);
    });

    return () => { alive = false; controller.abort(); };
    // The coordinate pair IS the identity here; `point` is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return place;
}

/** Test seam: forget what has been looked up. */
export function clearPlaceCache() {
  cache.clear();
}
